import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  const comic = comics.find((c) => c.slug === slug);
  const relatedComics = comics
    .filter((c) => c.slug !== slug && c.category === comic?.category)
    .slice(0, 4);

  const categorySlug = categories.find((c) => c.name === comic?.category)?.slug || '';

  if (!comic) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="text-2xl font-[family-name:var(--font-heading)] text-stone-800">漫画未找到</h1>
          <Link to="/" className="mt-4 inline-block text-sm text-stone-500 hover:text-stone-800">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const chapters = Array.from({ length: Math.floor(Math.random() * 50 + 30) }, (_, i) => ({
    num: i + 1,
    title: `第${i + 1}话`,
    date: new Date(2026, 5, 30 - Math.floor(i / 3)).toISOString().split('T')[0],
  })).reverse();

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${comic.title} - 韩漫屋韩国漫画在线免费阅读`,
            description: comic.description,
            author: { '@type': 'Person', name: comic.author },
            datePublished: '2024-01-15',
            dateModified: comic.updateDate,
            url: `http://98fire.com/comic/${slug}`,
            image: comic.cover,
            publisher: {
              '@type': 'Organization',
              name: '韩漫屋',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `http://98fire.com/comic/${slug}`,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'http://98fire.com/' },
              { '@type': 'ListItem', position: 2, name: comic.category, item: 'http://98fire.com/category/all' },
              { '@type': 'ListItem', position: 3, name: comic.title, item: `http://98fire.com/comic/${slug}` },
            ],
          }),
        }}
      />
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <main className="pt-24 pb-20">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10">
            <nav className="flex items-center gap-2 text-xs text-stone-400 mb-8" aria-label="面包屑导航">
              <Link to="/" className="hover:text-stone-600 transition-colors">首页</Link>
              <span>/</span>
              <Link to="/category/all" className="hover:text-stone-600 transition-colors">{comic.category}</Link>
              <span>/</span>
              <span className="text-stone-600">{comic.title}</span>
            </nav>

            <div className="flex flex-col md:flex-row gap-10 mb-16">
              <div className="md:w-[280px] flex-shrink-0">
                <div className="relative overflow-hidden rounded-2xl aspect-[3/4] bg-stone-100">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={comic.title + ' - 韩漫屋韩国漫画免费阅读'}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold font-[family-name:var(--font-heading)] text-stone-800">
                  {comic.title}
                </h1>

                <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-stone-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i> {comic.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-amber-400"></i>
                    <strong className="text-stone-800">{comic.rating}</strong> 分
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i> {(comic.views / 10000).toFixed(0)}万阅读
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-book-open-line"></i> {comic.latestChapter}
                  </span>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  <Link
                    to="/category/all"
                    className="px-3 py-1 rounded-full bg-stone-100 text-xs text-stone-600 hover:bg-stone-200 transition-colors whitespace-nowrap"
                  >
                    {comic.category}
                  </Link>
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-stone-50 border border-stone-200 text-xs text-stone-500 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-8 p-6 rounded-xl bg-white border border-stone-100">
                  <h2 className="text-sm font-semibold text-stone-800 mb-3">作品简介</h2>
                  <p className="text-sm text-stone-500 leading-relaxed">{comic.description}</p>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <button className="px-8 py-3 bg-stone-800 text-white rounded-full text-sm font-medium hover:bg-stone-700 transition-all duration-200 whitespace-nowrap cursor-pointer">
                    开始阅读 第1话
                  </button>
                  <button className="px-8 py-3 border border-stone-300 text-stone-600 rounded-full text-sm font-medium hover:bg-stone-50 transition-all duration-200 whitespace-nowrap cursor-pointer">
                    收藏漫画
                    <i className="ri-heart-line ml-1.5"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-lg font-bold font-[family-name:var(--font-heading)] text-stone-800 mb-6">
                章节列表
                <span className="ml-2 text-sm font-normal text-stone-400">共 {chapters.length} 话</span>
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[500px] overflow-y-auto pr-2">
                {chapters.map((ch) => (
                  <a
                    key={ch.num}
                    href="#"
                    className="flex items-center justify-between px-4 py-3 rounded-lg border border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50 transition-all duration-200 text-sm cursor-pointer group whitespace-nowrap"
                  >
                    <span className="text-stone-700 group-hover:text-stone-900 font-medium">
                      {ch.title}
                    </span>
                    <span className="text-[11px] text-stone-400">{ch.date}</span>
                  </a>
                ))}
              </div>
            </div>

            {relatedComics.length > 0 && (
              <div>
                <h2 className="text-lg font-bold font-[family-name:var(--font-heading)] text-stone-800 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                  {relatedComics.map((rc) => (
                    <Link
                      key={rc.id}
                      to={`/comic/${rc.slug}`}
                      className="group cursor-pointer"
                      data-product-shop=""
                    >
                      <div className="relative overflow-hidden rounded-xl bg-stone-100 mb-3 aspect-[3/4]">
                        <img
                          src={rc.cover}
                          alt={rc.title}
                          title={rc.title + ' - 韩漫屋韩国漫画'}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <h3 className="text-sm font-medium text-stone-800 group-hover:text-stone-600 transition-colors line-clamp-1">
                        {rc.title}
                      </h3>
                      <p className="mt-1 text-xs text-stone-400">
                        {rc.latestChapter} · {rc.author}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}